import React, { useState, useEffect } from 'react';
import { getCategories, updateAppCategories, getAppInfo } from '../../services/api';

function CategoryManager({ appDetails, appId, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [appInfo, setAppInfo] = useState(null);
  const [selectedPrimary, setSelectedPrimary] = useState('');
  const [selectedSecondary, setSelectedSecondary] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  console.log('CategoryManager - appDetails:', appDetails);

  useEffect(() => {
    if (appId) {
      loadData();
    }
  }, [appId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load categories
      const catResponse = await getCategories();
      console.log('Categories response:', catResponse.data);
      if (catResponse.data.success) {
        setCategories(catResponse.data.categories || []);
      }

      // Load app info
      if (appId) {
        const infoResponse = await getAppInfo(appId);
        console.log('App info response:', infoResponse.data);
        if (infoResponse.data.success) {
          if (infoResponse.data.appInfos && infoResponse.data.appInfos.length > 0) {
            const info = infoResponse.data.appInfos[0];
            console.log('App info object:', info);
            console.log('Relationships:', info.relationships);
            setAppInfo(info);
            
            // Set current categories
            if (info.relationships?.primaryCategory?.data?.id) {
              setSelectedPrimary(info.relationships.primaryCategory.data.id);
              console.log('Set primary category:', info.relationships.primaryCategory.data.id);
            } else {
              console.log('No primary category relationship found');
              setSelectedPrimary('');
            }
            if (info.relationships?.secondaryCategory?.data?.id) {
              setSelectedSecondary(info.relationships.secondaryCategory.data.id);
              console.log('Set secondary category:', info.relationships.secondaryCategory.data.id);
            } else {
              console.log('No secondary category relationship found');
              setSelectedSecondary('');
            }
          } else {
            console.log('No appInfos array in response');
            // Create a placeholder appInfo for saving
            setAppInfo({ id: null, relationships: {} });
          }
        } else {
          console.log('App info request was not successful');
        }
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedPrimary) {
      alert('Please select a primary category');
      return;
    }

    if (!appInfo || !appInfo.id) {
      alert('App information not available. Please try refreshing the page.');
      return;
    }

    setSaving(true);
    try {
      await updateAppCategories(
        appInfo.id,
        selectedPrimary,
        selectedSecondary || null
      );
      alert('Categories updated successfully!');
      setIsEditing(false);
      onUpdate();
    } catch (err) {
      alert('Failed to update categories: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset to original values
    if (appInfo?.relationships?.primaryCategory?.data) {
      setSelectedPrimary(appInfo.relationships.primaryCategory.data.id);
    } else {
      setSelectedPrimary('');
    }
    if (appInfo?.relationships?.secondaryCategory?.data) {
      setSelectedSecondary(appInfo.relationships.secondaryCategory.data.id);
    } else {
      setSelectedSecondary('');
    }
    setIsEditing(false);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category?.attributes?.name || categoryId;
  };

  if (loading) {
    return (
      <div className="loading-inline">
        <div className="spinner"></div>
      </div>
    );
  }

  const iosCategories = (categories || []).filter(cat => 
    cat.attributes?.platforms?.includes('IOS')
  );

  console.log('iOS Categories:', iosCategories);

  return (
    <div>
      <p className="text-secondary" style={{ marginBottom: '20px' }}>
        Choose the categories that best describe your app. The primary category is the main category 
        where your app appears in the App Store.
      </p>

      {!isEditing ? (
        // Read-only view
        <div>
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label className="form-label">Primary Category</label>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setIsEditing(true)}
              >
                Edit
              </button>
            </div>
            <div className="info-card">
              {selectedPrimary ? (
                <span style={{ fontWeight: '500' }}>
                  {getCategoryName(selectedPrimary)}
                </span>
              ) : (
                <span className="text-secondary" style={{ fontStyle: 'italic' }}>
                  {loading ? 'Loading...' : 'No primary category set in App Store Connect'}
                </span>
              )}
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label className="form-label">Secondary Category</label>
            <div className="info-card">
              {selectedSecondary ? (
                <span style={{ fontWeight: '500' }}>
                  {getCategoryName(selectedSecondary)}
                </span>
              ) : (
                <span className="text-secondary" style={{ fontStyle: 'italic' }}>
                  {loading ? 'Loading...' : 'No secondary category'}
                </span>
              )}
            </div>
          </div>
        </div>
      ) : (
        // Edit form
        <div>
          <div className="form-group">
            <label className="form-label">Primary Category *</label>
            <select
              className="form-select"
              value={selectedPrimary}
              onChange={(e) => setSelectedPrimary(e.target.value)}
              required
            >
              <option value="">Select a primary category</option>
              {iosCategories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.attributes.name || cat.id}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Secondary Category (Optional)</label>
            <select
              className="form-select"
              value={selectedSecondary}
              onChange={(e) => setSelectedSecondary(e.target.value)}
            >
              <option value="">None</option>
              {iosCategories
                .filter(cat => cat.id !== selectedPrimary)
                .map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.attributes.name || cat.id}
                  </option>
                ))}
            </select>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving || !selectedPrimary}
            >
              {saving ? 'Saving...' : 'Save Categories'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CategoryManager;
